import type { AnalysisType, ChartOptions } from "./dataAnalysisUtils";

/** Only for analysis types needing server-side computation; the rest go via buildPlotlyFigure. */
export function buildNumpyCode(
  data: number[] | number[][],
  analysisType: AnalysisType,
  options?: ChartOptions,
): string {
  const is2D = Array.isArray(data[0]);
  const dataStr = is2D
    ? `[${(data as number[][]).map((row) => `[${row.join(", ")}]`).join(", ")}]`
    : `[${(data as number[]).join(", ")}]`;

  switch (analysisType) {
    case "descriptive":
      return [
        "import json",
        "import numpy as np",
        `data = np.array(${dataStr}, dtype=float)`,
        "result = {",
        '  "count": int(len(data)),',
        '  "mean": float(np.mean(data)),',
        '  "median": float(np.median(data)),',
        '  "std": float(np.std(data, ddof=0)),',
        '  "variance": float(np.var(data, ddof=0)),',
        '  "min": float(np.min(data)),',
        '  "max": float(np.max(data)),',
        '  "q1": float(np.percentile(data, 25)),',
        '  "q3": float(np.percentile(data, 75)),',
        '  "iqr": float(np.percentile(data, 75) - np.percentile(data, 25))',
        "}",
        "json.dumps(result)",
      ].join("\n");

    case "histogram": {
      const bins =
        options?.histogramBins && options.histogramBins > 0
          ? String(options.histogramBins)
          : "'auto'";
      return [
        "import json",
        "import numpy as np",
        `data = np.array(${dataStr}, dtype=float)`,
        `counts, bin_edges = np.histogram(data, bins=${bins})`,
        "bins = ((bin_edges[:-1] + bin_edges[1:]) / 2).tolist()",
        'json.dumps({"bins": bins, "counts": counts.tolist()})',
      ].join("\n");
    }

    case "fft":
      return [
        "import json",
        "import numpy as np",
        `data = np.array(${dataStr}, dtype=float)`,
        "n = len(data)",
        "freqs = np.fft.rfftfreq(n).tolist()",
        "magnitudes = np.abs(np.fft.rfft(data)).tolist()",
        'json.dumps({"frequencies": freqs, "magnitudes": magnitudes})',
      ].join("\n");

    case "polyFit": {
      const degree = options?.polyDegree ?? 3;
      return [
        "import json",
        "import numpy as np",
        `data = np.array(${dataStr}, dtype=float)`,
        "n = len(data)",
        "x = np.arange(n, dtype=float)",
        `degree = min(${degree}, n - 1)`,
        "coeffs = np.polyfit(x, data, degree)",
        "x_fine = np.linspace(0, n - 1, max(n, 100))",
        "fitted = np.polyval(coeffs, x_fine)",
        "predicted = np.polyval(coeffs, x)",
        "ss_res = np.sum((data - predicted) ** 2)",
        "ss_tot = np.sum((data - np.mean(data)) ** 2)",
        "r_squared = float(1 - ss_res / ss_tot) if ss_tot > 0 else 1.0",
        'json.dumps({"x": x_fine.tolist(), "fitted": fitted.tolist(), "original": data.tolist(), "degree": int(degree), "r_squared": r_squared})',
      ].join("\n");
    }

    case "correlation":
      return [
        "import json",
        "import numpy as np",
        `data = np.array(${dataStr}, dtype=float)`,
        "corr = np.corrcoef(data, rowvar=False)",
        "cols = data.shape[1]",
        'labels = [f"Col {i+1}" for i in range(cols)]',
        'json.dumps({"matrix": corr.tolist(), "labels": labels})',
      ].join("\n");

    default:
      // line, bar, boxplot, scatter, scatter3d, heatmap, surface, contour — no numpy needed
      return "";
  }
}
